import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type NewsletterSignup = Tables<'newsletter_signups'>;

interface NewsletterSignupResponse {
  success: boolean;
  message: string;
  action: string;
}
import { useToast } from '@/hooks/use-toast';

export const useNewsletter = () => {
  const [signups, setSignups] = useState<NewsletterSignup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSignups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('newsletter_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching newsletter signups:', error);
        setError(error.message);
        return;
      }

      setSignups(data || []);
    } catch (error) {
      console.error('Error fetching newsletter signups:', error);
      setError('Failed to fetch newsletter signups');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNewsletter = async (
    email: string, 
    source: string = 'footer',
    metadata?: Record<string, any>
  ): Promise<NewsletterSignupResponse | null> => {
    setLoading(true);
    
    try {
      const { data, error } = await supabase.rpc('handle_newsletter_signup', {
        p_email: email,
        p_source: source,
        p_metadata: metadata || {}
      });

      if (error) {
        console.error('Newsletter signup error:', error);
        toast({
          title: "Error",
          description: "Failed to subscribe to newsletter. Please try again.",
          variant: "destructive",
        });
        return null;
      }

      const result = data as unknown as NewsletterSignupResponse;
      
      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
        });
        // Refresh signups list after successful subscription
        await fetchSignups();
      } else {
        toast({
          title: "Notice",
          description: result.message,
          variant: "default",
        });
      }

      return result;
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe to newsletter. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getNewsletterSignups = async (): Promise<NewsletterSignup[]> => {
    try {
      const { data, error } = await supabase
        .from('newsletter_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching newsletter signups:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching newsletter signups:', error);
      return [];
    }
  };

  const deleteNewsletterSignup = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('newsletter_signups')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting newsletter signup:', error);
        toast({
          title: "Error",
          description: "Failed to delete newsletter signup.",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setSignups(prev => prev.filter(signup => signup.id !== id));

      toast({
        title: "Success",
        description: "Newsletter signup deleted successfully.",
      });
      return true;
    } catch (error) {
      console.error('Error deleting newsletter signup:', error);
      toast({
        title: "Error",
        description: "Failed to delete newsletter signup.",
        variant: "destructive",
      });
      return false;
    }
  };

  const toggleNewsletterSignupStatus = async (id: string, isActive: boolean): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('newsletter_signups')
        .update({ is_active: isActive })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating newsletter signup:', error);
        toast({
          title: "Error",
          description: "Failed to update newsletter signup status.",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setSignups(prev => prev.map(signup => 
        signup.id === id ? { ...signup, is_active: isActive } : signup
      ));

      toast({
        title: "Success",
        description: `Newsletter signup ${isActive ? 'activated' : 'deactivated'} successfully.`,
      });
      return true;
    } catch (error) {
      console.error('Error updating newsletter signup:', error);
      toast({
        title: "Error",
        description: "Failed to update newsletter signup status.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Fetch signups on component mount
  useEffect(() => {
    fetchSignups();
  }, []);

  return {
    signups,
    loading,
    error,
    fetchSignups,
    subscribeToNewsletter,
    getNewsletterSignups,
    deleteNewsletterSignup,
    toggleNewsletterSignupStatus,
  };
}; 